import React from "react";
import {
  MapPin,
  Star,
  Truck,
  MessageCircle,
  Wallet,
  CreditCard,
  Building2,
  Sparkles,
  ShieldCheck,
  Palette,
  Car,
  Clock,
  PaintBucket,
  CheckCircle2,
  ArrowRight,
  Phone,
  Instagram,
  Droplets,
  Waves,
  Brush,
  Heart,
} from "lucide-react";
import {
  WhatsAppCTA,
  FloatingWhatsApp,
  WA_LINK,
} from "@/components/WhatsAppButton";

const LOGO = "/assets/logo.png";
const HERO_IMG = "/assets/parede.jpg";
const MACHINE_IMG = "/assets/maquina.png";
const PARKING_IMG = "/assets/estacionamento.png";
const WALL_IMG = "/assets/parede.jpg";
const CANS_IMG = "/assets/parede.jpg";

/* ---------------- HEADER ---------------- */
const Header = () => (
  <header
    data-testid="site-header"
    className="sticky top-0 z-40 backdrop-blur-xl bg-black/80 border-b-2 border-[#FDC614]/25 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)]"
  >
    {/* Top thin marketing bar */}
    <div className="bg-[#FDC614] text-black text-[10px] sm:text-xs font-extrabold uppercase tracking-wider sm:tracking-widest">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-1.5 flex items-center justify-center gap-2 sm:gap-4 flex-wrap text-center">
        <span className="inline-flex items-center gap-1.5">
          <MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
          <span className="hidden md:inline">
            Estrada União Indústria 2334, Corrêas, Petrópolis-RJ (Próximo ao Posto 2)
          </span>
          <span className="md:hidden">Corrêas, Petrópolis-RJ</span>
        </span>
        <span className="hidden md:inline">•</span>
        <span className="inline-flex items-center gap-1.5">
          <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" /> (24) 2020-5797
        </span>
        <span className="hidden sm:inline">•</span>
        <span className="hidden sm:inline-flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> Seg-Sex 7h-17h · Sáb 7h30-12h
        </span>
      </div>
    </div>

    {/* Main header — mobile: simple flex with logo center; desktop: 3-col grid */}
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex sm:grid sm:grid-cols-3 items-center py-2 sm:py-4 gap-2 sm:gap-3">
      {/* Left: brand tag (hidden on mobile to free space) */}
      <div className="hidden sm:flex flex-col items-start text-[11px] uppercase tracking-widest text-white/60 font-bold leading-tight">
        <span className="text-[#FDC614]">Vai pintar?</span>
        <span className="text-white/80">#casadastintasresolve</span>
      </div>

      {/* Center: BIG transparent logo */}
      <a
        href="#top"
        data-testid="header-logo"
        className="flex items-center justify-center flex-1 sm:col-start-2"
      >
        <img
          src={LOGO}
          alt="Casa das Tintas"
          fetchpriority="high"
          loading="eager"
          className="h-16 sm:h-24 md:h-28 lg:h-32 w-auto max-w-full object-contain drop-shadow-[0_10px_24px_rgba(253,198,20,0.45)] sm:-mb-5"
        />
      </a>

      {/* Right: WhatsApp CTA */}
      <div className="flex justify-end shrink-0">
        <WhatsAppCTA
          size="md"
          variant="primary"
          testId="header-whatsapp-cta"
          className="!px-3 !py-2.5 !text-xs sm:!px-5 sm:!py-3 sm:!text-sm"
        >
          <span className="hidden sm:inline">Fale Conosco</span>
          <span className="sm:hidden">WhatsApp</span>
        </WhatsAppCTA>
      </div>
    </div>
  </header>
);

/* ---------------- HERO (Bloco 1) ---------------- */
const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative overflow-hidden bg-[#0A0A0A] brush-bg"
  >
    {/* Yellow diagonal accent */}
    <div className="absolute -top-32 -right-32 w-[480px] h-[480px] bg-[#FDC614] opacity-10 rotate-45 blur-3xl pointer-events-none" />
    <div className="absolute top-1/3 -left-20 w-72 h-72 bg-[#FDC614] opacity-[0.06] rounded-full blur-3xl pointer-events-none" />

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-20 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center relative">
      <div className="lg:col-span-7 fade-up">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 sm:mb-6 border border-[#FDC614]/30 bg-[#FDC614]/5 text-[#FDC614] text-[10px] sm:text-xs font-bold uppercase tracking-widest">
          <Sparkles className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0" />
          A melhor loja de tintas em Petrópolis
        </div>

        <h1 className="font-black text-white text-3xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1] tracking-tighter">
          A SOLUÇÃO COMPLETA <br className="hidden sm:block" />
          PARA A SUA <span className="text-[#FDC614]">PINTURA.</span>
        </h1>

        <p className="mt-5 sm:mt-6 text-sm sm:text-lg text-white/70 max-w-xl leading-relaxed">
          Mais do que uma loja de tintas — somos{" "}
          <span className="text-white font-bold">
            especialistas em materiais de pintura
          </span>
          . Resolvemos qualquer necessidade: dos produtos certos até a escolha de
          cores.
        </p>

        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
          <WhatsAppCTA size="xl" variant="primary" testId="hero-whatsapp-cta" className="w-full sm:w-auto">
            Falar no WhatsApp Agora
          </WhatsAppCTA>
          <a
            href="#diferenciais"
            data-testid="hero-scroll-link"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 sm:py-5 font-extrabold text-white/80 hover:text-[#FDC614] transition-colors uppercase tracking-tight text-xs sm:text-sm"
          >
            Ver vantagens
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* 3 destaque tiles */}
        <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div
            className="bg-[#141414] border border-[#FDC614]/20 p-4 sm:p-5 card-depth"
            data-testid="hero-stat-cores"
          >
            <div className="text-2xl sm:text-3xl font-black text-[#FDC614] leading-none">
              4.000+
            </div>
            <div className="text-xs sm:text-sm text-white font-bold uppercase tracking-wide mt-2 leading-tight">
              Tons feitos na hora
            </div>
          </div>
          <div
            className="bg-[#FDC614] text-black p-4 sm:p-5 card-depth-yellow"
            data-testid="hero-stat-marcas"
          >
            <div className="text-base sm:text-lg font-black leading-tight">
              As melhores marcas
            </div>
            <div className="text-xs sm:text-sm font-bold uppercase tracking-wide mt-2 leading-tight">
              e os melhores preços
            </div>
          </div>
          <div
            className="bg-[#141414] border border-[#FDC614]/20 p-4 sm:p-5 card-depth"
            data-testid="hero-stat-entrega"
          >
            <div className="text-base sm:text-lg font-black text-[#FDC614] leading-tight">
              Entrega rápida
            </div>
            <div className="text-xs sm:text-sm text-white font-bold uppercase tracking-wide mt-2 leading-tight">
              em toda Petrópolis
            </div>
            <div className="text-[10px] text-white/40 mt-1">
              *consulte as condições
            </div>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5 relative fade-up mt-6 lg:mt-0">
        <div className="relative aspect-[9/16] w-full max-w-[280px] sm:max-w-xs lg:max-w-[320px] mx-auto">
          <div className="absolute inset-0 bg-[#FDC614] -rotate-3 border-2 border-black" />

          {/* Video block (loop, muted, autoplay, no audio).
              Container aspect 9/16 matches the source video — no letterbox
              bars, no cropping; the full frame is visible on every screen. */}
          <video
            data-testid="hero-video"
            poster={HERO_IMG}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="relative w-full h-full object-cover border-2 border-white/10 rotate-2 shadow-2xl bg-black"
            aria-label="Vídeo Casa das Tintas"
          >
            <source
              src="/assets/video-hero.mp4"
              type="video/mp4"
            />
          </video>

          {/* 3D layered review badge */}
          <div
            data-testid="hero-review-badge"
            className="absolute -bottom-6 left-2 sm:-bottom-8 sm:-left-10 max-w-[200px] sm:max-w-[260px]"
          >
            <div
              className="relative bg-black px-3 py-2.5 sm:px-5 sm:py-4 border-2 border-[#FDC614]"
              style={{
                boxShadow:
                  "0 6px 0 0 #FDC614, 0 8px 0 0 #000, 0 18px 30px -8px rgba(253,198,20,0.45), 0 22px 40px -10px rgba(0,0,0,0.7)",
              }}
            >
              <div className="flex items-center gap-1 text-[#FDC614] mb-1.5">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star
                    key={s}
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#FDC614] drop-shadow-[0_2px_4px_rgba(253,198,20,0.7)]"
                  />
                ))}
              </div>
              <div
                className="text-white text-[11px] sm:text-sm font-extrabold leading-tight uppercase tracking-tight"
                style={{
                  textShadow:
                    "0 2px 0 #000, 0 4px 8px rgba(253,198,20,0.35)",
                }}
              >
                Avaliada por dezenas
                <br />
                de clientes em Petrópolis
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- DIFERENCIAIS (Bloco 2) ---------------- */
const differentials = [
  {
    icon: MessageCircle,
    title: "Atendimento via WhatsApp",
    desc: "Atendimento consultivo: tira dúvidas, indica produto certo, ajuda na escolha da cor, calcula a quantidade ideal e entrega na sua obra.",
    big: true,
    accent: true,
  },
  {
    icon: ShieldCheck,
    title: "Consultoria Técnica",
    desc: "Desde a infiltração até a tinta que dá pra limpar. Vamos te indicar o melhor produto pro seu projeto e que cabe no seu orçamento.",
  },
  {
    icon: Palette,
    title: "Consultoria de Cores",
    desc: "Entendemos seus gostos, visualizamos seu projeto, sugerimos cores e entregamos a cor perfeita. Mais de 4 mil cores à disposição.",
  },
  {
    icon: Wallet,
    title: "Desconto exclusivo à vista",
    desc: "Pagou à vista, levou mais barato. Condição especial em todos os produtos.",
  },
  {
    icon: CreditCard,
    title: "Parcelamento no cartão",
    desc: "Divida sua compra no cartão com facilidade para caber no seu orçamento.",
  },
  {
    icon: Building2,
    title: "Parceria com Condomínios e CNPJ",
    desc: "Preços diferenciados, consultoria condominial, faturamento com boleto e prazos.",
    big: true,
  },
];

const Differentials = () => (
  <section
    id="diferenciais"
    data-testid="differentials-section"
    className="bg-[#0A0A0A] py-20 sm:py-28 relative"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end mb-10 sm:mb-12">
        <div className="lg:col-span-7">
          <div className="text-[#FDC614] text-[10px] sm:text-xs font-extrabold uppercase tracking-[0.25em] sm:tracking-[0.3em] mb-3">
            Por que Casa das Tintas
          </div>
          <h2 className="font-black text-white text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-tighter">
            Mais do que tinta. <br />
            <span className="text-[#FDC614]">Ajudamos a entregar o seu sonho.</span>
          </h2>
        </div>
        <p className="lg:col-span-5 text-white/60 text-sm sm:text-base leading-relaxed">
          Atendimento humano, consultoria de verdade e condições de pagamento que
          cabem em qualquer obra — em casa ou no canteiro.
        </p>
      </div>

      {/* Bento grid - 6 items in 3 columns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {differentials.map((d, i) => {
          const Icon = d.icon;
          const isAccent = d.accent;
          return (
            <div
              key={i}
              data-testid={`differential-card-${i}`}
              className={`group relative p-6 sm:p-8 border ${
                isAccent
                  ? "bg-[#FDC614] text-black border-[#FDC614] card-depth-yellow"
                  : "bg-[#141414] text-white border-white/10 hover:border-[#FDC614]/40 card-depth"
              } transition-all duration-300 hover:-translate-y-2 min-h-[240px] flex flex-col justify-between overflow-hidden`}
            >
              <Icon
                className={`w-10 h-10 ${
                  isAccent ? "text-black" : "text-[#FDC614]"
                }`}
                strokeWidth={2}
              />
              <div className="mt-6">
                <h3
                  className={`font-black text-xl sm:text-2xl leading-tight tracking-tight ${
                    isAccent ? "text-black" : "text-white"
                  }`}
                >
                  {d.title}
                </h3>
                <p
                  className={`mt-2 text-sm leading-relaxed ${
                    isAccent ? "text-black/80" : "text-white/60"
                  }`}
                >
                  {d.desc}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 text-center">
        <WhatsAppCTA size="lg" variant="primary" testId="differentials-cta">
          Quero o atendimento consultivo
        </WhatsAppCTA>
      </div>
    </div>
  </section>
);

/* ---------------- MARCAS (Bloco 3) ---------------- */
const brands = [
  {
    name: "Sparlack",
    src: "/assets/marcas/sparlack.png",
  },
  {
    name: "Hydronorth",
    src: "/assets/marcas/hydronorth.png",
  },
  {
    name: "Tintas Elit",
    src: "/assets/marcas/elit.png",
  },
  {
    name: "Coral",
    src: "/assets/marcas/coral.png",
  },
  {
    name: "Suvinil",
    src: "/assets/marcas/suvinil.png",
  },
  {
    name: "Marca 1",
    src: "/assets/marcas/1.png",
  },
  {
    name: "Marca 2",
    src: "/assets/marcas/2.png",
  },
  {
    name: "Marca 3",
    src: "/assets/marcas/3.png",
  },
  {
    name: "Marca 4",
    src: "/assets/marcas/4.png",
  },
  {
    name: "Marca 5",
    src: "/assets/marcas/5.png",
  },
  {
    name: "Marca 7",
    src: "/assets/marcas/7.png",
  },
  {
    name: "Marca 8",
    src: "/assets/marcas/8.png",
  },
  {
    name: "Marca 10",
    src: "/assets/marcas/10.png",
  },
  {
    name: "Marca 12",
    src: "/assets/marcas/12.png",
  },
  {
    name: "Marca 13",
    src: "/assets/marcas/13.png",
  },
  {
    name: "Marca 14",
    src: "/assets/marcas/14.png",
  },
  {
    name: "Marca 17",
    src: "/assets/marcas/17.png",
  },
  {
    name: "Marca 19",
    src: "/assets/marcas/19.png",
  },
  {
    name: "Marca 20",
    src: "/assets/marcas/20.png",
  },
  {
    name: "Marca 21",
    src: "/assets/marcas/21.png",
  },
];



const Brands = () => (
  <section
    data-testid="brands-section"
    className="bg-white py-14 sm:py-16 border-y-4 border-[#FDC614] overflow-hidden relative"
  >
    {/* Subtle yellow glow accents */}
    <div className="absolute -top-20 left-1/4 w-72 h-72 bg-[#FDC614] opacity-[0.12] rounded-full blur-3xl pointer-events-none" />
    <div className="absolute -bottom-20 right-1/4 w-72 h-72 bg-[#FDC614] opacity-[0.10] rounded-full blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 sm:mb-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-black/60 text-[10px] sm:text-xs font-extrabold uppercase tracking-[0.25em] sm:tracking-[0.3em] mb-2">
            Trabalhamos com as melhores
          </div>
          <h2 className="font-black text-black text-2xl sm:text-4xl lg:text-5xl leading-[1] tracking-tighter">
            MARCAS LÍDERES <span className="text-[#FDC614]" style={{ WebkitTextStroke: "1.5px #000", paintOrder: "stroke fill" }}>DO MERCADO.</span>
          </h2>
        </div>
        <p className="text-black/70 text-sm sm:text-base max-w-md">
          Linhas completas para pintura residencial, comercial e imobiliária.
        </p>
      </div>
    </div>

    {/* Marquee — uma única fila contínua, igual no mobile e no desktop */}
    <div className="relative overflow-hidden">
      <div className="flex animate-marquee-mobile sm:animate-marquee whitespace-nowrap will-change-transform items-center py-2 sm:py-4">
        {[...brands, ...brands].map((b, i) => (
          <div
            key={i}
            data-testid={`brand-logo-${i}`}
            className="mx-2 sm:mx-6 shrink-0 flex items-center justify-center bg-white overflow-hidden border-2 border-black p-2 w-[140px] h-[96px] sm:w-[260px] sm:h-[180px]"
            style={{
              boxShadow: "0 4px 0 0 #000, 0 10px 20px -6px rgba(0,0,0,0.35)",
            }}
          >
            <img
              src={b.src}
              alt={b.name}
              loading="eager"
              decoding="async"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- SOLUÇÕES (Bloco 4) ---------------- */
const Solutions = () => (
  <section
    data-testid="solutions-section"
    className="bg-[#0A0A0A] py-20 sm:py-28 relative"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mb-10 sm:mb-16">
        <div className="text-[#FDC614] text-[10px] sm:text-xs font-extrabold uppercase tracking-[0.25em] sm:tracking-[0.3em] mb-3">
          Soluções completas
        </div>
        <h2 className="font-black text-white text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-tighter">
          Cada problema tem uma <span className="text-[#FDC614]">tinta certa.</span>
        </h2>
        <p className="mt-4 text-white/60 text-sm sm:text-base leading-relaxed">
          Mofo, infiltração, ferrugem, tinta branca que amarela? A gente tem
          solução pra cada situação.
        </p>
      </div>

      {/* Color machine - full width banner */}
      <div
        data-testid="solution-color-machine"
        className="relative overflow-hidden border border-white/10 bg-[#141414] group hover:border-[#FDC614]/40 transition-colors min-h-[360px] sm:min-h-[480px] mb-4 sm:mb-5 card-depth"
      >
        <img
          src={MACHINE_IMG}
          alt="Máquina de fabricação de cores"
          className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-10 lg:max-w-2xl">
          <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 mb-4 bg-[#FDC614] text-black text-[10px] sm:text-xs font-extrabold uppercase tracking-widest">
            <Palette className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            Destaque
          </div>
          <h3 className="font-black text-white text-2xl sm:text-4xl lg:text-5xl leading-[1] tracking-tighter">
            Mais de <span className="text-[#FDC614]">4.000 cores</span>
            <br />
            feitas na hora.
          </h3>
          <p className="mt-3 sm:mt-4 text-white/75 max-w-xl text-sm sm:text-base">
            Sistema de tinturação: você escolhe a cor e a gente produz na hora.
            Cores{" "}
            <span className="text-white font-bold">
              Suvinil, Coral, Sherwin-Williams, Pantone
            </span>
            ... Personalização total para seu projeto.
          </p>
          <div className="mt-5 sm:mt-6">
            <WhatsAppCTA size="md" variant="primary" testId="solution-color-cta">
              Pedir minha cor agora
            </WhatsAppCTA>
          </div>
        </div>
      </div>

      {/* 4 problem cards in 2x2 grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        <div
          data-testid="solution-white-paint"
          className="bg-[#141414] border border-white/10 p-7 sm:p-8 hover:border-[#FDC614]/40 transition-all hover:-translate-y-2 group card-depth"
        >
          <Sparkles className="w-10 h-10 text-[#FDC614]" />
          <h3 className="mt-5 font-black text-white text-2xl sm:text-3xl leading-tight tracking-tight">
            Tinta branca que amarela com o tempo?
          </h3>
          <p className="mt-3 text-white/70 text-sm sm:text-base leading-relaxed">
            Temos{" "}
            <span className="text-[#FDC614] font-bold">
              tinta branca que não amarela
            </span>{" "}
            e deixa sua casa nova por mais tempo!
          </p>
        </div>

        <div
          data-testid="solution-infiltracao"
          className="bg-[#FDC614] text-black p-7 sm:p-8 hover:-translate-y-2 transition-transform group card-depth-yellow"
        >
          <Droplets className="w-10 h-10 text-black" />
          <h3 className="mt-5 font-black text-2xl sm:text-3xl leading-tight tracking-tight">
            Infiltrações?
          </h3>
          <p className="mt-3 text-black/80 text-sm sm:text-base leading-relaxed">
            Manta líquida, resinas, borracha líquida, silicones — tudo pra
            <span className="font-extrabold"> resolver o problema de vez.</span>
          </p>
        </div>

        <div
          data-testid="solution-antimold"
          className="bg-[#FDC614] text-black p-7 sm:p-8 hover:-translate-y-2 transition-transform group card-depth-yellow"
        >
          <ShieldCheck className="w-10 h-10 text-black" />
          <h3 className="mt-5 font-black text-2xl sm:text-3xl leading-tight tracking-tight">
            Problema com mofo?
          </h3>
          <p className="mt-3 text-black/80 text-sm sm:text-base leading-relaxed">
            <span className="font-extrabold">
              Temos tinta anti-mofo e massa que bloqueiam a umidade.
            </span>{" "}
            Acabe com o mofo e a umidade. Tintas específicas com proteção
            antifúngica de longa duração.
          </p>
        </div>

        <div
          data-testid="solution-antirust"
          className="bg-[#141414] border border-white/10 p-7 sm:p-8 hover:border-[#FDC614]/40 transition-all hover:-translate-y-2 group card-depth"
        >
          <PaintBucket className="w-10 h-10 text-[#FDC614]" />
          <h3 className="mt-5 font-black text-white text-2xl sm:text-3xl leading-tight tracking-tight">
            Apareceu ferrugem?
          </h3>
          <p className="mt-3 text-white/70 text-sm sm:text-base leading-relaxed">
            <span className="text-[#FDC614] font-bold">
              Tinta especial que aplica direto sobre ferrugem
            </span>{" "}
            — sem lixar, sem fundo, com proteção que dura anos.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- LOCALIZAÇÃO (Bloco 5) ---------------- */
const Location = () => (
  <section
    data-testid="location-section"
    className="bg-[#0A0A0A] py-20 sm:py-28 border-t border-white/5"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-6 fade-up">
        <div className="text-[#FDC614] text-[10px] sm:text-xs font-extrabold uppercase tracking-[0.25em] sm:tracking-[0.3em] mb-3">
          Onde estamos
        </div>
        <h2 className="font-black text-white text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-tighter">
          Em Corrêas, Petrópolis. <br />
          <span className="text-[#FDC614]">Com estacionamento na porta.</span>
        </h2>

        <div className="mt-8 space-y-4">
          <div className="flex items-start gap-4 p-5 bg-[#141414] border border-white/10">
            <div className="w-11 h-11 flex items-center justify-center bg-[#FDC614] text-black shrink-0">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold">
                Endereço
              </div>
              <div className="text-white font-bold mt-1 leading-tight">
                Estr. União e Indústria, 2334 <br />
                Corrêas, Petrópolis-RJ — CEP 25720-062
              </div>
              <div className="text-white/60 text-sm mt-1">
                Próximo ao Posto 2
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4 p-5 bg-[#141414] border border-white/10">
            <div className="w-11 h-11 flex items-center justify-center bg-[#FDC614] text-black shrink-0">
              <Car className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold">
                Diferencial
              </div>
              <div className="text-white font-bold mt-1">
                Estacionamento gratuito na porta da loja
              </div>
              <div className="text-white/60 text-sm mt-1">
                Pare, escolha sua tinta com calma e leve direto pro carro.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-4 p-5 bg-[#141414] border border-white/10">
            <div className="w-11 h-11 flex items-center justify-center bg-[#FDC614] text-black shrink-0">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold">
                Horário
              </div>
              <div className="text-white font-bold mt-1">
                Seg a Sex · 8h às 18h
              </div>
              <div className="text-white/60 text-sm">Sábado · 8h às 13h</div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <WhatsAppCTA size="lg" variant="primary" testId="location-whatsapp-cta">
            Falar com a loja
          </WhatsAppCTA>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Estr.+Uni%C3%A3o+e+Ind%C3%BAstria%2C+2334+-+Corr%C3%AAas%2C+Petr%C3%B3polis+-+RJ%2C+25720-062"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="location-route-link"
            className="btn-3d btn-3d--outline inline-flex items-center justify-center gap-2 px-6 py-4 font-extrabold uppercase tracking-tight text-sm"
          >
            <MapPin className="w-4 h-4 relative z-10" />
            <span className="relative z-10">Traçar rota</span>
          </a>
        </div>
      </div>

      <div className="lg:col-span-6 fade-up">
        <div className="relative">
          <div className="aspect-[4/3] w-full overflow-hidden border-2 border-[#FDC614]/30">
            <iframe
              title="Mapa Casa das Tintas"
              src="https://www.google.com/maps?q=Estr.+Uni%C3%A3o+e+Ind%C3%BAstria%2C+2334+-+Corr%C3%AAas%2C+Petr%C3%B3polis+-+RJ&output=embed"
              className="w-full h-full grayscale-[20%]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <img
              src={PARKING_IMG}
              alt="Estacionamento na porta"
              className="w-full aspect-[4/3] object-cover border border-white/10"
            />
            <img
              src={WALL_IMG}
              alt="Parede pintada com cores"
              className="w-full aspect-[4/3] object-cover border border-white/10"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- PROVAS SOCIAIS (Bloco 6) ---------------- */
const reviews = [
  {
    name: "Walace Gomes",
    text: "Comprei um produto chegou rápido, me atendeu super bem no que eu estava precisando e vou comprar novamente assim que puder.",
    initials: "WG",
    url: "https://share.google/49tAcvTTbfq3MbeHr",
  },
  {
    name: "Juliana Senna",
    text: "Super recomendo a loja! Atendimento excepcional, preço justo e entrega com muita facilidade. Uma das melhores da cidade!!",
    initials: "JS",
    url: "https://share.google/RmMbb00Ygo2FNZXTH",
  },
  {
    name: "Cantor Ronald — Oficial",
    text: "Atendimento maravilhoso! Todos os produtos são de excelente qualidade e o preço é justo. O melhor de tudo é que também fazem atendimento via WhatsApp e fazem entrega. Muito obrigado!",
    initials: "CR",
    url: "https://share.google/mo9J1wPPaGIoWOXRn",
  },
  {
    name: "Raianne Nunes Dutra",
    text: "Ótimo atendimento e bom preço. Tive a explicação de todos os produtos para a melhor escolha.",
    initials: "RD",
    url: "https://share.google/krBxxIuiyEtaHJj3K",
  },
  {
    name: "Jairo Nicodemos",
    text: "Minha experiência foi excelente. Bom preço, ótimo atendimento, atendem pelo WhatsApp e fazem a entrega. Precisa melhor? Claro que não.",
    initials: "JN",
    url: "https://share.google/1uNFS9NxFZnbTV7rZ",
  },
  {
    name: "Kathleen Araújo",
    text: "Ótimo atendimento, atenciosamente com o cliente e praticidade! Quero agradecer especialmente ao atendente Caio por toda paciência e cuidado!",
    initials: "KA",
    url: "https://share.google/CXTpRxXfFOVvmbJMg",
  },
];

const ReviewCard = ({ name, text, initials, idx, url }) => (
  <div
    data-testid={`review-card-${idx}`}
    className="relative bg-[#0A0A0A] border-2 border-black p-6 sm:p-8 flex flex-col h-full hover:-translate-y-2 transition-transform duration-300 group"
    style={{ boxShadow: "0 10px 0 0 #000, 0 24px 50px -10px rgba(0,0,0,0.6)" }}
  >
    {/* Decorative quote mark */}
    <div
      aria-hidden="true"
      className="absolute -top-2 right-4 sm:right-5 text-[#FDC614] font-black leading-none select-none pointer-events-none"
      style={{ fontFamily: "'Outfit', sans-serif", fontSize: "100px", opacity: 0.18 }}
    >
      "
    </div>

    <div className="flex items-center gap-1 text-[#FDC614] mb-4 sm:mb-5 relative">
      {[0, 1, 2, 3, 4].map((s) => (
        <Star
          key={s}
          className="w-4 h-4 sm:w-5 sm:h-5 fill-[#FDC614] drop-shadow-[0_2px_4px_rgba(253,198,20,0.6)]"
        />
      ))}
    </div>
    <p className="text-white text-sm sm:text-[15px] leading-relaxed flex-1 relative font-medium">
      "{text}"
    </p>
    <div className="mt-6 sm:mt-7 flex items-center gap-3 pt-5 border-t-2 border-dashed border-white/15 relative">
      <div
        className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#FDC614] text-black font-black flex items-center justify-center text-sm sm:text-base shrink-0"
        style={{
          boxShadow:
            "0 3px 0 0 #A78508, 0 2px 0 0 rgba(255,255,255,0.5) inset, 0 8px 16px -4px rgba(253, 198, 20, 0.5)",
        }}
      >
        {initials}
      </div>
      <div className="min-w-0">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`review-name-link-${idx}`}
          className="text-white hover:text-[#FDC614] font-extrabold text-sm leading-tight transition-colors inline-flex items-center gap-1.5 group/link"
        >
          <span className="truncate">{name}</span>
          <ArrowRight className="w-3.5 h-3.5 -rotate-45 shrink-0 opacity-60 group-hover/link:opacity-100 transition-opacity" />
        </a>
        <div className="text-white/50 text-xs flex items-center gap-1 mt-0.5">
          <svg className="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Avaliação Google · ver original
        </div>
      </div>
    </div>
  </div>
);

const Reviews = () => (
  <section
    data-testid="reviews-section"
    className="relative bg-[#FDC614] py-24 sm:py-32 overflow-hidden border-y-[6px] border-black"
  >
    {/* Decorative paint splashes */}
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
      <div className="splatter-dot top-10 left-[8%] w-6 h-6 bg-black/15" />
      <div className="splatter-dot top-32 left-[18%] w-3 h-3 bg-black/20" />
      <div className="splatter-dot bottom-20 left-[5%] w-10 h-10 bg-black/10" />
      <div className="splatter-dot top-40 right-[10%] w-8 h-8 bg-black/15" />
      <div className="splatter-dot bottom-32 right-[6%] w-4 h-4 bg-black/20" />
      <div className="splatter-dot bottom-10 right-[20%] w-5 h-5 bg-black/15" />
      {/* Large yellow ring background */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] border-[40px] border-black/5 rounded-full" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] border-[40px] border-black/5 rounded-full" />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Section header — punchy and distinct */}
      <div className="text-center mb-12 sm:mb-20">
        <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 mb-5 bg-black text-[#FDC614] text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.25em] sm:tracking-[0.3em] border-2 border-black"
          style={{ boxShadow: "0 6px 0 0 rgba(0,0,0,0.4)" }}>
          <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-[#FDC614]" />
          Quem comprou aprovou
          <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-[#FDC614]" />
        </div>
        <h2 className="font-black text-black text-3xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tighter">
          Petrópolis confia
          <br />
          na{" "}
          <span className="relative inline-block">
            <span className="relative z-10 text-[#FDC614]" style={{ WebkitTextStroke: "2px #000", paintOrder: "stroke fill" }}>
              Casa das Tintas.
            </span>
          </span>
        </h2>

        {/* 5.0 rating badge */}
        <div className="mt-8 sm:mt-10 inline-flex items-center gap-3 sm:gap-4 bg-black text-white px-5 sm:px-6 py-3 sm:py-4 border-2 border-black"
          style={{ boxShadow: "0 8px 0 0 rgba(0,0,0,0.3), 0 16px 30px -8px rgba(0,0,0,0.4)" }}>
          <div className="text-4xl sm:text-6xl font-black text-[#FDC614] leading-none">5.0</div>
          <div className="text-left">
            <div className="flex gap-0.5 text-[#FDC614]">
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} className="w-4 h-4 sm:w-5 sm:h-5 fill-[#FDC614]" />
              ))}
            </div>
            <div className="text-white/70 text-[9px] sm:text-[10px] mt-1 font-extrabold uppercase tracking-[0.25em]">
              Avaliações Google
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-7">
        {reviews.map((r, i) => (
          <ReviewCard key={i} idx={i} {...r} />
        ))}
      </div>

      <div className="mt-12 sm:mt-16 text-center">
        <div className="max-w-2xl mx-auto mb-6">
          <h3 className="font-black text-black text-xl sm:text-3xl lg:text-4xl leading-tight tracking-tighter">
            Envie seu orçamento sem compromisso agora e confira
          </h3>
        </div>
        <WhatsAppCTA size="xl" variant="dark" testId="reviews-cta" className="w-full sm:w-auto">
          Quero comprar minha tinta agora
        </WhatsAppCTA>
        <div className="mt-4 text-black/70 text-xs sm:text-sm font-bold inline-flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Atendimento consultivo · resposta rápida
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- FINAL CTA STRIP ---------------- */
const FinalCTA = () => (
  <section
    data-testid="final-cta-section"
    className="relative bg-[#0A0A0A] py-20 sm:py-28 overflow-hidden border-t-[6px] border-black"
  >
    {/* Diagonal yellow accent */}
    <div className="absolute -top-20 -left-20 w-96 h-96 bg-[#FDC614] opacity-[0.08] rotate-12 blur-3xl pointer-events-none" />
    <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-[#FDC614] opacity-[0.06] -rotate-12 blur-3xl pointer-events-none" />

    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 sm:mb-6 border border-[#FDC614]/40 bg-[#FDC614]/5 text-[#FDC614] text-[10px] sm:text-xs font-extrabold uppercase tracking-[0.25em] sm:tracking-[0.3em]">
        Última chamada
      </div>
      <h2 className="font-black text-white text-3xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
        Pronto pra começar
        <br />
        <span className="text-[#FDC614]">sua pintura?</span>
      </h2>
      <p className="mt-4 sm:mt-5 text-white/65 text-sm sm:text-lg max-w-xl mx-auto">
        Fale agora com um especialista da Casa das Tintas e receba atendimento
        prioritário no WhatsApp.
      </p>

      {/* Arrow indicators pointing down to CTA */}
      <div className="mt-8 sm:mt-10 flex items-center justify-center gap-3 sm:gap-4 text-[#FDC614]">
        <svg
          viewBox="0 0 100 40"
          className="hidden sm:block w-20 sm:w-24 h-8 sm:h-10 float-y"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M5 5 C 30 30, 60 30, 90 25" />
          <path d="M82 18 L92 25 L82 32" />
        </svg>
        <div className="font-black text-sm sm:text-lg uppercase tracking-tight animate-pulse">
          Clique abaixo
        </div>
        <svg
          viewBox="0 0 100 40"
          className="hidden sm:block w-20 sm:w-24 h-8 sm:h-10 float-y -scale-x-100"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M5 5 C 30 30, 60 30, 90 25" />
          <path d="M82 18 L92 25 L82 32" />
        </svg>
      </div>

      <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
        <WhatsAppCTA size="xl" variant="primary" testId="final-whatsapp-cta" className="w-full sm:w-auto">
          Falar com um especialista
        </WhatsAppCTA>
      </div>
      <div className="mt-5 flex items-center justify-center gap-2 text-white/60 text-xs sm:text-sm font-bold">
        <CheckCircle2 className="w-4 h-4 text-[#FDC614]" />
        Resposta rápida · Atendimento humano
      </div>
    </div>
  </section>
);

/* ---------------- FOOTER ---------------- */
const Footer = () => (
  <footer
    data-testid="site-footer"
    className="bg-black border-t border-white/10 py-10 sm:py-12"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
      {/* Logo + brand */}
      <div className="flex flex-col items-center md:items-start gap-3 text-center md:text-left">
        <img
          src={LOGO}
          alt="Casa das Tintas"
          className="h-14 sm:h-16 w-auto object-contain drop-shadow-[0_4px_12px_rgba(253,198,20,0.3)]"
        />
        <div className="text-white/50 text-[10px] sm:text-xs font-bold uppercase tracking-widest">
          Sparlack · Hydronorth · Tintas Elit · Coral · Suvinil
        </div>
      </div>

      {/* Contact */}
      <div className="text-white/70 text-sm text-center space-y-2">
        <a
          href={WA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-phone-link"
          className="inline-flex items-center gap-2 text-white hover:text-[#FDC614] transition-colors font-bold"
        >
          <Phone className="w-4 h-4 text-[#FDC614]" />
          (24) 2020-5797
        </a>
        <div className="text-white/50 text-xs">
          Seg-Sex 7h-17h · Sáb 7h30-12h
        </div>
        <a
          href="https://www.instagram.com/casadastintas_petropolis/"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-instagram-link"
          className="inline-flex items-center gap-2 text-white hover:text-[#FDC614] transition-colors font-bold text-sm"
        >
          <Instagram className="w-4 h-4 text-[#FDC614]" />
          @casadastintas_petropolis
        </a>
      </div>

      {/* Copyright */}
      <div className="text-white/40 text-[11px] sm:text-xs text-center md:text-right leading-relaxed">
        © 2026 Casa das Tintas
        <br />
        Estrada União Indústria, 2334
        <br />
        Corrêas, Petrópolis-RJ (Próximo ao Posto 2)
      </div>
    </div>
  </footer>
);

/* ---------------- PAGE ---------------- */
export default function LandingPage() {
  return (
    <div data-testid="landing-page" className="bg-[#0A0A0A] text-white min-h-screen">
      <Header />
      <main>
        <Hero />
        <Differentials />
        <Brands />
        <Solutions />
        <Location />
        <Reviews />
        <FinalCTA />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
